import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import axios from "axios";

export default ({ column, onChange, initialData }) => {
    const [inputValue, setInputValue] = useState(initialData?.[column.companyNameField]);
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isListVisible, setIsListVisible] = useState(false);
    const latestQueryRef = useRef(inputValue);
    const minSearchLength = 3;

    const fetchSuggestions = async (query) => {
        if (query.length < minSearchLength) {
            setSuggestions([]);
            setIsListVisible(false);
            return;
        }

        setIsLoading(true);
        setSuggestions([]);
        latestQueryRef.current = query

        try {
            const response = await axios.get(`/searchCompanies`, {
                params: {
                    query: query
                }
            });
            const data = response.data;
            if (query === latestQueryRef.current) {
                setSuggestions(data);
                setIsListVisible(data.length > 0);
            }

        } catch (err) {
            console.error(err)
        } finally {
            if (query === latestQueryRef.current) {
                setIsLoading(false);
            }
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetchSuggestions = useCallback(
        debounce(fetchSuggestions, 300),
        [inputValue]
    );

    const handleInputChange = (event) => {
        const value = event.target.value;
        setInputValue(value);

        if (value.length === 0) {
            setSuggestions([]);
            setIsListVisible(false);
            setIsLoading(false);
            debouncedFetchSuggestions.cancel();
            latestQueryRef.current = '';
        } else if (value.length >= minSearchLength) {
            setIsLoading(true);
            debouncedFetchSuggestions(value);
        } else {
            setSuggestions([]);
            setIsListVisible(false);
            setIsLoading(false);
            debouncedFetchSuggestions.cancel();
            latestQueryRef.current = value;
        }
    };

    const handleSelectCompany = (company) => {
        setInputValue(company.companyName);
        setSuggestions([]);
        setIsListVisible(false);
        const fieldValues = {
            companyNameField: company.companyName,
            sirenField: company.siren,
            siretField: company.siret,
            addressField: company.address,
            cityField: company.city,
            zipCodeField: company.zipCode,
            activityNumberField: company.activityNumber,
        }
        const data = {}
        for (const key in fieldValues) {
            if (column[key]) {
                data[column[key]] = fieldValues[key]
            }
        }
        onChange(data);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.company-input-container')) {
                setIsListVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div
            className={`company-input-container`}
            style={{ position: 'relative' }}
        >
            <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => {
                    if (suggestions.length > 0 && inputValue.length >= minSearchLength) {
                        setIsListVisible(true);
                    }
                }}
                placeholder={"Rechercher une entreprise..."}
                aria-autocomplete="list"
                aria-expanded={isListVisible}
                style={{ width: '100%', boxSizing: 'border-box' }}
            />

            {isLoading && <div style={styles.loader}>Chargement...</div>}

            {isListVisible && suggestions.length > 0 && (
                <ul style={styles.suggestionList}>
                    {suggestions.map((company) => (
                        <li
                            key={company.id}
                            style={styles.suggestionItem}
                            onClick={() => handleSelectCompany(company)}
                            onMouseDown={(e) => e.preventDefault()}
                            role="option"
                            aria-selected="false"
                        >
                            <div style={styles.companyName}>{company.companyName}</div>
                            <div style={styles.companyAddress}>{company.siren} - {company.address}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const styles = {
    loader: {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '0.8em',
        color: '#888',
    },
    suggestionList: {
        listStyleType: 'none',
        padding: 0,
        margin: 0,
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        border: '1px solid #ccc',
        backgroundColor: 'white',
        maxHeight: '200px',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    suggestionItem: {
        padding: '8px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #eee',
    },
    companyName: {
        fontWeight: 'bold',
    },
    companyAddress: {
        fontSize: '0.9em',
        color: '#555',
    },
};